/*
 * ---------------------------------------------------------------------------------------------------------------------
 * APEX CART GIFT SYNC
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * Puts the gift announced by the cart drawer (snippets/apex-cart-gift.liquid) into the cart for real, so it reaches
 * the checkout instead of only being promised. The gift is a product priced at zero in Shopify.
 *
 * The rule: exactly one gift line, quantity 1, whenever the cart holds at least one product the customer chose (and,
 * when the drawer is set to a minimum, when those products reach it). Otherwise no gift line at all.
 *
 * The gift line carries the `_apex_gift` line item property. The leading underscore hides it from the customer at
 * checkout while keeping it on the order, and it is how the drawer and the tiers bar tell the gift apart.
 *
 * Settings come from the #apex-gift-config JSON that sections/cart-drawer.liquid prints on every page. It is read
 * again on each check, because the drawer re-renders itself whenever the cart changes.
 */

(function () {
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // One failed write is enough: without this, the same error would be retried on every cart event.
  var disabled = false;

  // How many gift units the cart holds right now, so the count bubbles can leave them out.
  var giftQuantity = 0;

  // Guards against the checks our own writes trigger through cart:change / cart:refresh.
  var running = false;

  function readConfig() {
    var node = document.getElementById('apex-gift-config');

    if (!node) {
      return null;
    }

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function isGift(item) {
    return Boolean(item.properties && item.properties._apex_gift);
  }

  function countGifts(cart) {
    return cart.items.reduce(function (total, item) {
      return isGift(item) ? total + item.quantity : total;
    }, 0);
  }

  /*
   * The theme's <cart-count> bubbles show cart.item_count, which includes the gift line: two pairs of shoes would
   * read "3". Its setter is wrapped so every update (cart:change, cart:refresh, pageshow) takes the gift out. The
   * first paint already comes right from Liquid (snippets/apex-cart-real-count.liquid).
   */
  if (window.customElements) {
    customElements.whenDefined('cart-count').then(function () {
      var proto = customElements.get('cart-count').prototype;
      var descriptor = Object.getOwnPropertyDescriptor(proto, 'itemCount');

      if (!descriptor || !descriptor.set || proto.__apexGiftAware) {
        return;
      }

      Object.defineProperty(proto, 'itemCount', {
        configurable: true,
        get: descriptor.get,
        set: function (count) {
          descriptor.set.call(this, Math.max(0, count - giftQuantity));
        }
      });

      proto.__apexGiftAware = true;
    });
  }

  // Capture phase on window runs before the bubbles' own listeners on document, so the number is fresh when they ask.
  window.addEventListener(
    'cart:change',
    function (event) {
      var cart = event.detail && event.detail.cart;

      if (cart && cart.items) {
        giftQuantity = countGifts(cart);
      }
    },
    true
  );

  function post(url, body) {
    return fetch(root + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(function (response) {
      if (!response.ok) {
        throw new Error(url + ' responded ' + response.status);
      }
      return response.json();
    });
  }

  // The drawer and the cart page already listen for this and pull the cart again.
  function refresh() {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function earnsGift(config, cart) {
    var realItems = cart.items.filter(function (item) {
      return !isGift(item);
    });

    if (realItems.length === 0) {
      return false;
    }

    if (config.condition === 'threshold') {
      // Before discounts, or reaching a discount could take the cart back under the minimum and drop the gift.
      var subtotal = realItems.reduce(function (total, item) {
        return total + item.original_line_price;
      }, 0);

      var rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;

      return subtotal >= config.threshold * rate;
    }

    return true;
  }

  function reconcile(cart) {
    var config = readConfig();

    // No drawer on this page, or nothing set up: leave the cart alone.
    if (!config || disabled || running || !cart || !cart.items) {
      return Promise.resolve();
    }

    var giftLines = cart.items.filter(isGift);
    var wanted = Boolean(config.enabled && config.variantId) && earnsGift(config, cart);

    giftQuantity = countGifts(cart);

    // A gift line that is the right variant, alone, and at quantity 1 is already correct.
    var correct =
      giftLines.length === 1 &&
      giftLines[0].quantity === 1 &&
      String(giftLines[0].variant_id) === String(config.variantId);

    if ((wanted && correct) || (!wanted && giftLines.length === 0)) {
      return Promise.resolve();
    }

    running = true;

    // Clear whatever gift lines are there, then add a clean one if the cart earns it. Removing first also repairs a
    // customer who changed the gift quantity by hand on the cart page.
    var work = giftLines.reduce(function (chain, line) {
      return chain.then(function () {
        return post('cart/change.js', { id: line.key, quantity: 0 });
      });
    }, Promise.resolve());

    if (wanted) {
      work = work.then(function () {
        return post('cart/add.js', {
          items: [{ id: config.variantId, quantity: 1, properties: { _apex_gift: 'true' } }]
        });
      });
    }

    return work
      .then(function () {
        // Known before the refresh, so the bubbles redraw with the gift already taken out.
        giftQuantity = wanted ? 1 : 0;
        refresh();
      })
      .catch(function (error) {
        // Most likely the gift went out of stock or was unpublished. Stop rather than loop.
        disabled = true;
        console.warn('[apex-gift] the gift could not be synced, skipping:', error.message);
      })
      .finally(function () {
        running = false;
      });
  }

  function reconcileFromServer() {
    if (disabled || running || !readConfig()) {
      return;
    }

    fetch(root + 'cart.js')
      .then(function (response) {
        return response.json();
      })
      .then(reconcile)
      .catch(function () {
        /* offline or blocked: the next cart event will try again */
      });
  }

  document.addEventListener('cart:change', function (event) {
    var cart = event.detail && event.detail.cart;

    if (cart && cart.items) {
      reconcile(cart);
    } else {
      reconcileFromServer();
    }
  });

  document.addEventListener('cart:refresh', function () {
    // Our own refresh lands here too; the running flag and the "already correct" check keep it from writing again.
    setTimeout(reconcileFromServer, 0);
  });

  var initialConfig = readConfig();

  if (initialConfig && initialConfig.giftQuantity) {
    giftQuantity = initialConfig.giftQuantity;
  }

  // First load, and the back/forward cache, where no cart event fires at all.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcileFromServer);
  } else {
    reconcileFromServer();
  }

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      reconcileFromServer();
    }
  });
})();
